/** PRO-19 Entreprise (informations, adresses) · PRO-20 Utilisateurs de l'entreprise (CLIENT_ADMIN : inviter ★, rôle, désactivation). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Choice, Modal, SelectField, Table, Tabs, TextField, useToast } from '@myron/design-system';
import { MapPin, Plus, UserPlus } from 'lucide-react';
import type { S } from '@myron/contracts';
import { ApiErreur, del, get, patch, post, put } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete, StatutBadge } from '../../../shared/ui/composants';
import { dateHeure, LIBELLE_MOYEN } from '../../../shared/ui/format';

type Onglet = 'infos' | 'adresses' | 'utilisateurs';
const LIBELLE_ROLE_CLIENT: Record<string, string> = { CLIENT_ADMIN: 'Administrateur', CLIENT_ACHETEUR: 'Acheteur', CLIENT_COMPTABLE: 'Comptabilité' };

export function PageEntreprise({ onglet: initial = 'infos' }: { onglet?: Onglet }) {
  const { peut } = useSession();
  const [onglet, setOnglet] = useState<Onglet>(initial);
  const onglets: { id: Onglet; libelle: string }[] = [{ id: 'infos', libelle: 'Informations' }, { id: 'adresses', libelle: 'Adresses' }];
  if (peut('entreprise.utilisateurs.gerer')) onglets.push({ id: 'utilisateurs', libelle: 'Utilisateurs' });
  const actif = onglets.some((o) => o.id === onglet) ? onglet : 'infos';
  return (
    <>
      <EntetePage titre="Mon entreprise" fil={[{ libelle: 'Portail', vers: '/pro' }, { libelle: 'Entreprise' }]} />
      <Tabs<Onglet> label="Sections de l’entreprise" onglets={onglets} actif={actif} onChange={setOnglet} />
      <div role="tabpanel">
        {actif === 'infos' && <Informations />}
        {actif === 'adresses' && <Adresses />}
        {actif === 'utilisateurs' && <Utilisateurs />}
      </div>
    </>
  );
}

function Informations() {
  const q = useQuery({ queryKey: ['pro', 'entreprise'], queryFn: () => get<S<'EntreprisePro'>>('/pro/entreprise') });
  return (
    <Card>
      <EtatRequete requete={q} enfants={(e) => (
        <dl className="my-dl">
          <dt>Raison sociale</dt><dd>{e.raisonSociale}</dd>
          {e.nomCommercial && <><dt>Nom commercial</dt><dd>{e.nomCommercial}</dd></>}
          <dt>Référence client</dt><dd>{e.reference}</dd>
          <dt>SIRET</dt><dd>{e.siret}</dd>
          <dt>Statut</dt><dd><StatutBadge statut={e.statut ?? ''} /></dd>
          <dt>Moyens de paiement autorisés</dt><dd>{(e.moyensPaiementAutorises ?? []).map((m) => LIBELLE_MOYEN[m]).join(', ') || '—'}</dd>
        </dl>
      )} />
    </Card>
  );
}

// ───────────────────────────────── Adresses ─────────────────────────────────
type Adresse = S<'Adresse'>;
const ADRESSE_VIDE = { type: 'LIVRAISON' as 'LIVRAISON' | 'FACTURATION', libelle: '', ligne1: '', ligne2: '', codePostal: '', ville: '', contact: '', telephone: '', consignesLivraison: '', parDefaut: false };

function Adresses() {
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const gerer = peut('entreprise.adresses.gerer');
  const q = useQuery({ queryKey: ['pro', 'adresses'], queryFn: () => get<Adresse[]>('/pro/entreprise/adresses') });
  const [edition, setEdition] = useState<{ id?: string; version?: number; v: typeof ADRESSE_VIDE } | null>(null);
  const [suppression, setSuppression] = useState<Adresse | null>(null);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const enregistrer = useMutation({
    mutationFn: () => {
      const corps = Object.fromEntries(Object.entries(edition!.v).filter(([, x]) => x !== ''));
      return edition!.id ? put(`/pro/entreprise/adresses/${edition!.id}`, corps, { version: edition!.version }) : post('/pro/entreprise/adresses', corps);
    },
    onSuccess: () => { setEdition(null); void qc.invalidateQueries({ queryKey: ['pro', 'adresses'] }); toast({ ton: 'succes', titre: 'Adresse enregistrée' }); },
    onError: (e) => { if (e instanceof ApiErreur) setErreurs(e.champs); },
  });
  const supprimer = useMutation({
    mutationFn: (a: Adresse) => del(`/pro/entreprise/adresses/${a.id}`, { version: a.version }),
    onSuccess: () => { setSuppression(null); void qc.invalidateQueries({ queryKey: ['pro', 'adresses'] }); },
  });
  const ouvrir = (a?: Adresse) => {
    setErreurs({});
    setEdition(a ? { id: a.id, version: a.version, v: { ...ADRESSE_VIDE, ...Object.fromEntries(Object.entries(a).filter(([k]) => k in ADRESSE_VIDE)) } as typeof ADRESSE_VIDE } : { v: { ...ADRESSE_VIDE } });
  };
  const champ = (k: keyof typeof ADRESSE_VIDE, label: string, o: { requis?: boolean; aide?: string } = {}) => (
    <TextField label={label} required={o.requis} aide={o.aide} value={String(edition?.v[k] ?? '')} erreur={erreurs[k]} onChange={(e) => setEdition((x) => x && { ...x, v: { ...x.v, [k]: e.target.value } })} />
  );
  return (
    <div className="my-pile">
      {gerer && <div><Button icone={Plus} onClick={() => ouvrir()}>Ajouter une adresse de livraison</Button></div>}
      <EtatRequete requete={q} vide={(l) => !l.length} titreVide="Aucune adresse" enfants={(l) => (
        <div className="my-grille">
          {l.map((a) => (
            <Card key={a.id} as="article" aria-label={`Adresse ${a.libelle ?? a.ligne1}`}>
              <div className="my-pile my-pile--serre">
                <div className="my-ligne my-ligne--espace">
                  <strong>{a.libelle || (a.type === 'FACTURATION' ? 'Facturation' : 'Livraison')}</strong>
                  <span className="my-ligne">{a.type === 'FACTURATION' ? <Badge ton="sable">Facturation</Badge> : <Badge ton="info">Livraison</Badge>}{a.parDefaut && <Badge ton="marque">Par défaut</Badge>}</span>
                </div>
                <span>{a.ligne1}{a.ligne2 ? `, ${a.ligne2}` : ''}</span>
                <span>{a.codePostal} {a.ville}</span>
                {a.consignesLivraison && <span className="my-petit my-secondaire">{a.consignesLivraison}</span>}
                {gerer && (
                  <div className="my-ligne">
                    <Button compact variante="secondaire" onClick={() => ouvrir(a)}>Modifier</Button>
                    {a.type !== 'FACTURATION' && <Button compact variante="tertiaire" onClick={() => setSuppression(a)}>Supprimer</Button>}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )} />
      <Modal ouverte={!!edition} titre={edition?.id ? 'Modifier l’adresse' : 'Nouvelle adresse'} onFermer={() => setEdition(null)} large
        actions={<><Button variante="tertiaire" onClick={() => setEdition(null)}>Annuler</Button><Button icone={MapPin} chargement={enregistrer.isPending} onClick={() => enregistrer.mutate()}>Enregistrer</Button></>}>
        <div className="my-form">
          {champ('libelle', 'Libellé (ex. Restaurant, Entrepôt)')}
          {champ('ligne1', 'Adresse', { requis: true })}
          {champ('ligne2', 'Complément')}
          <div className="my-form-ligne">{champ('codePostal', 'Code postal', { requis: true })}{champ('ville', 'Ville', { requis: true })}</div>
          <div className="my-form-ligne">{champ('contact', 'Contact sur place')}{champ('telephone', 'Téléphone du contact')}</div>
          {champ('consignesLivraison', 'Consignes de livraison', { aide: 'Accès poids lourd, horaires de réception…' })}
          <Choice label="Adresse par défaut" checked={!!edition?.v.parDefaut} onChange={(e) => setEdition((x) => x && { ...x, v: { ...x.v, parDefaut: e.target.checked } })} />
          {enregistrer.isError && !Object.keys(erreurs).length ? <ErreurFormulaire erreur={enregistrer.error} /> : null}
        </div>
      </Modal>
      <Confirmation ouverte={!!suppression} titre="Supprimer cette adresse ?" texte={suppression ? `${suppression.ligne1}, ${suppression.codePostal} ${suppression.ville}` : ''} libelleAction="Supprimer" danger
        onConfirmer={() => suppression && supprimer.mutate(suppression)} onFermer={() => setSuppression(null)} chargement={supprimer.isPending} erreur={supprimer.error} />
    </div>
  );
}

// ───────────────────────────────── Utilisateurs ─────────────────────────────────
type UtilisateurClient = S<'UtilisateurClient'>;

function Utilisateurs() {
  const qc = useQueryClient();
  const toast = useToast();
  const { session } = useSession();
  const q = useQuery({ queryKey: ['pro', 'utilisateurs'], queryFn: () => get<UtilisateurClient[]>('/pro/entreprise/utilisateurs') });
  const [invitation, setInvitation] = useState<{ email: string; prenom: string; nom: string; role: string } | null>(null);
  const [desactivation, setDesactivation] = useState<UtilisateurClient | null>(null);
  const inviter = useMutation({
    mutationFn: () => post<UtilisateurClient>('/pro/entreprise/utilisateurs', { email: invitation!.email.trim(), role: invitation!.role, ...(invitation!.prenom ? { prenom: invitation!.prenom } : {}), ...(invitation!.nom ? { nom: invitation!.nom } : {}) }),
    onSuccess: (u) => { setInvitation(null); void qc.invalidateQueries({ queryKey: ['pro', 'utilisateurs'] }); toast({ ton: 'succes', titre: 'Invitation envoyée', texte: u.email }); },
  });
  const modifier = useMutation({
    mutationFn: (v: { u: UtilisateurClient; role?: string; statut?: string }) => patch<UtilisateurClient>(`/pro/entreprise/utilisateurs/${v.u.id}`, { ...(v.role ? { role: v.role } : {}), ...(v.statut ? { statut: v.statut } : {}) }, { version: v.u.version }),
    onSuccess: () => { setDesactivation(null); void qc.invalidateQueries({ queryKey: ['pro', 'utilisateurs'] }); toast({ ton: 'succes', titre: 'Utilisateur mis à jour' }); },
  });
  const optionsRole = Object.entries(LIBELLE_ROLE_CLIENT).map(([value, label]) => ({ value, label }));
  return (
    <div className="my-pile">
      <div><Button icone={UserPlus} onClick={() => setInvitation({ email: '', prenom: '', nom: '', role: 'CLIENT_ACHETEUR' })}>Inviter un utilisateur</Button></div>
      <ErreurFormulaire erreur={modifier.isError && !desactivation ? modifier.error : null} />
      <EtatRequete requete={q} enfants={(l) => (
        <Table<UtilisateurClient>
          legende="Utilisateurs de l’entreprise"
          cle={(u) => u.id!}
          lignes={l}
          colonnes={[
            { cle: 'n', titre: 'Utilisateur', rendu: (u) => <span className="my-pile my-pile--serre"><strong>{`${u.prenom ?? ''} ${u.nom ?? ''}`.trim() || '—'}</strong><span className="my-petit my-secondaire">{u.email}</span></span> },
            {
              cle: 'r', titre: 'Rôle', rendu: (u) => u.id === session?.utilisateur.id ? LIBELLE_ROLE_CLIENT[u.role ?? ''] : (
                <select className="my-input" aria-label={`Rôle de ${u.email}`} value={u.role} disabled={u.statut === 'DESACTIVE' || modifier.isPending} onChange={(e) => modifier.mutate({ u, role: e.target.value })}>
                  {optionsRole.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ),
            },
            { cle: 's', titre: 'Statut', rendu: (u) => <StatutBadge statut={u.statut === 'INVITE' ? 'EN_ATTENTE' : u.statut === 'ACTIF' ? 'ACTIF' : 'INACTIF'} /> },
            { cle: 'm', titre: 'MFA', rendu: (u) => (u.mfaActive ? <Badge ton="succes">Activée</Badge> : <Badge>Non</Badge>) },
            { cle: 'c', titre: 'Dernière connexion', rendu: (u) => dateHeure(u.derniereConnexionLe) },
            {
              cle: 'a', titre: 'Actions', actions: true, rendu: (u) => u.id === session?.utilisateur.id ? <span className="my-petit my-secondaire">Vous</span> : u.statut === 'DESACTIVE'
                ? <Button compact variante="secondaire" onClick={() => modifier.mutate({ u, statut: 'ACTIF' })}>Réactiver</Button>
                : <Button compact variante="tertiaire" onClick={() => setDesactivation(u)} aria-label={`Désactiver ${u.email}`}>Désactiver</Button>,
            },
          ]}
        />
      )} />
      <Modal ouverte={!!invitation} titre="Inviter un utilisateur" onFermer={() => setInvitation(null)}
        actions={<><Button variante="tertiaire" onClick={() => setInvitation(null)}>Annuler</Button><Button chargement={inviter.isPending} disabled={!invitation?.email} onClick={() => inviter.mutate()}>Envoyer l’invitation</Button></>}>
        <div className="my-form">
          <TextField label="Adresse e-mail" type="email" required value={invitation?.email ?? ''} onChange={(e) => setInvitation((x) => x && { ...x, email: e.target.value })} erreur={inviter.error instanceof ApiErreur ? inviter.error.champs.email : undefined} />
          <div className="my-form-ligne">
            <TextField label="Prénom" value={invitation?.prenom ?? ''} onChange={(e) => setInvitation((x) => x && { ...x, prenom: e.target.value })} />
            <TextField label="Nom" value={invitation?.nom ?? ''} onChange={(e) => setInvitation((x) => x && { ...x, nom: e.target.value })} />
          </div>
          <SelectField label="Rôle" value={invitation?.role ?? 'CLIENT_ACHETEUR'} onChange={(e) => setInvitation((x) => x && { ...x, role: e.target.value })} options={optionsRole} />
          <p className="my-petit my-secondaire">Action sensible : une confirmation d’identité récente peut être demandée.</p>
          {inviter.isError && !(inviter.error instanceof ApiErreur && inviter.error.champs.email) ? <ErreurFormulaire erreur={inviter.error} /> : null}
        </div>
      </Modal>
      <Confirmation ouverte={!!desactivation} titre="Désactiver cet utilisateur ?" texte={`${desactivation?.email ?? ''} ne pourra plus se connecter ; ses sessions sont fermées immédiatement.`} libelleAction="Désactiver" danger
        onConfirmer={() => desactivation && modifier.mutate({ u: desactivation, statut: 'DESACTIVE' })} onFermer={() => setDesactivation(null)} chargement={modifier.isPending} erreur={modifier.error} />
    </div>
  );
}
